import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { clsx } from 'clsx';
import { feedbackService } from '../../services/api';

interface InvestigationFeedbackProps {
    chain: string;
    address: string;
    investigationData: any;
}

export const InvestigationFeedback: React.FC<InvestigationFeedbackProps> = ({ chain, address, investigationData }) => {
    const [isAccurate, setIsAccurate] = useState<boolean | null>(null);
    const [comments, setComments] = useState('');
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    const handleSubmit = async () => {
        if (isAccurate === null) return;

        setStatus('submitting');
        try {
            await feedbackService.submit(
                chain,
                address,
                isAccurate,
                comments.trim() || undefined,
                investigationData
            );
            setStatus('success');
            // Hide the success message after 3 seconds
            setTimeout(() => setStatus('idle'), 3000);
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            setStatus('error');
        }
    };

    if (status === 'success') {
        return (
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-md text-sm text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-4 h-4" />
                <span>Thank you! Your feedback helps improve our analysis models.</span>
            </div>
        );
    }

    return (
        <div className="p-3 bg-secondary/30 border border-border rounded-md space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Was this assessment accurate?</span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsAccurate(true)}
                        className={clsx(
                            'p-1.5 rounded-md transition-colors',
                            isAccurate === true
                                ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                                : 'hover:bg-secondary text-muted-foreground'
                        )}
                        title="Yes, the findings look correct"
                    >
                        <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setIsAccurate(false)}
                        className={clsx(
                            'p-1.5 rounded-md transition-colors',
                            isAccurate === false
                                ? 'bg-red-500/20 text-red-600 dark:text-red-400'
                                : 'hover:bg-secondary text-muted-foreground'
                        )}
                        title="No, something looks wrong"
                    >
                        <ThumbsDown className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {isAccurate !== null && (status === 'idle' || status === 'submitting' || status === 'error') && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <textarea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder={isAccurate ? "Any other thoughts? (Optional)" : "What did we get wrong? (Optional but helpful)"}
                        className="w-full text-sm p-2 bg-background border border-input rounded-md resize-none h-16 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <div className="flex justify-end items-center gap-2">
                        {status === 'error' && (
                            <span className="text-xs text-red-500">Failed to send. Please try again.</span>
                        )}
                        <button
                            onClick={handleSubmit}
                            disabled={status === 'submitting'}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                            {status === 'submitting' ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <Send className="w-3.5 h-3.5" />
                            )}
                            Submit Feedback
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
